/**
 * Resumen del carrito: envío por zona (settings del tema) y armado opcional, en líneas separadas.
 * Las funciones puras se prueban en tests/cart-summary.test.mjs. El cotizador calc-armado no cambia.
 * Descuentos por cantidad (ADR 0008): «products» es el subtotal original y «discount» la suma real de los descuentos de
 * línea de Shopify; el armado que falta se estima con la escala de armado (globalThis.Packs, assets/packs.js), el ya
 * comprado va con su precio real.
 * Por transferencia (T-041, ADR 0017): Packs.priceLines sobre las líneas con custom.packs (packLines del JSON), con el escalón
 * compartido por familia; el total por transferencia es el total estimado menos (lo que cobra el carrito por esas líneas − su
 * precio por transferencia del escalón × cantidad). Envío, armado y líneas sin packs se pagan igual.
 */

/** Escala de armado parseada (vacía sin packs.js o sin setting). */
function scaleOf(data) {
  const Packs = globalThis.Packs;
  return Packs && data.assemblyScale ? Packs.parseScale(data.assemblyScale) : [];
}

/** Precio de envío para una zona. Devuelve null si la zona no existe. */
export function shippingFor(zoneId, zones, productsSubtotal, freeFrom = 0) {
  const zone = zones.find((item) => item.id === zoneId);
  if (!zone) return null;
  const free = freeFrom > 0 && productsSubtotal >= freeFrom;
  return { id: zone.id, name: zone.name, price: free ? 0 : Math.max(0, Number(zone.price) || 0), free };
}

/** Unidades de armado que faltan comprar y su precio. Con escala, el precio por unidad baja según el total de unidades
 * (estimado: las que faltan se cobran a la tarifa del escalón; las ya compradas van con su precio real del carrito). */
export function assemblyFor(data, enabled, scale = scaleOf(data)) {
  const Packs = globalThis.Packs;
  const units = Math.ceil(Math.max(0, Number(data.units) || 0) - 1e-9);
  const existing = Math.max(0, Number(data.existing) || 0);
  const missing = Math.max(0, units - existing);
  const total = Math.max(units, existing);
  const unit = Packs && scale.length ? Packs.unitPrice(data.price, total, scale) : data.price;
  const price = enabled ? data.serviceTotal + missing * unit : data.serviceTotal;
  return { units, existing, missing, price, unit, discounted: unit < data.price, enabled, canToggle: units > 0 && data.available && (data.stock === null || data.stock >= missing) };
}

/** Diferencia entre lo que cobra el carrito por las líneas con packs (final, con los descuentos de Shopify) y su precio por
 * transferencia del escalón de su familia; null si ninguna línea tiene precio por transferencia. */
export function transferSaving(lines) {
  const Packs = globalThis.Packs;
  if (!Packs || !Array.isArray(lines) || !lines.length) return null;
  const priced = Packs.priceLines(lines);
  if (!priced.hasTransfer) return null;
  return priced.lines.reduce((sum, line, i) => (line.transferSubtotal === null ? sum : sum + (lines[i].final ?? line.subtotal) - line.transferSubtotal), 0);
}

/** «N% OFF» del botón del carrito (T-049): total web de los productos (lo que cobra el carrito por cada línea) contra su total
 * por transferencia (escalón de su familia; las líneas sin precio por transferencia pagan lo mismo), sin envío ni armado.
 * 0 sin ninguna línea con precio por transferencia. */
export function transferPercentOf(lines) {
  const Packs = globalThis.Packs;
  const saving = transferSaving(lines);
  if (saving === null) return 0;
  const priced = Packs.priceLines(lines);
  const web = priced.lines.reduce((sum, line, i) => sum + (lines[i].final ?? line.subtotal), 0);
  return Packs.transferPercent(web, web - saving);
}

/** Mensaje de WhatsApp para pagar el carrito por transferencia (T-045, ADR 0018): todas las líneas (cantidad × transferencia del
 * escalón de su familia, Packs.transferOrder; sin precio por transferencia, lo que cobra el carrito), el envío de la zona elegida
 * (o «a coordinar»), el armado si suma algo y el total por transferencia. → { text, total, url } o null sin número de WhatsApp,
 * sin packs.js o sin ninguna línea con precio por transferencia. */
export function transferMessage(data, summary) {
  const Packs = globalThis.Packs;
  if (!Packs || !data.whatsapp || summary.transferTotal === null) return null;
  const text = data.text || {};
  const order = Packs.transferOrder(data.packLines || []);
  const shipping = summary.shipping ? { name: summary.shipping.name, price: summary.shipping.price, free: summary.shipping.free } : { pending: true };
  const a = summary.assembly;
  const assembly = a.price > 0 ? { units: a.enabled ? Math.max(a.units, a.existing) : a.existing, price: a.price } : null;
  const strings = { intro: text.wa_intro, line: text.wa_line, shipping: text.wa_shipping, shipping_free: text.wa_shipping_free, shipping_pending: text.wa_shipping_pending, assembly: text.wa_assembly, total: text.wa_total, outro: text.wa_outro };
  const message = Packs.transferText({ order, shipping, assembly }, strings, data.moneyWithCurrency);
  return { ...message, url: Packs.whatsappUrl(data.whatsapp, message.text) };
}

/** Totales: productos − descuentos por cantidad + envío + armado. El armado comprado ya está en el carrito, así que siempre suma. */
export function summarize(data, zoneId, assemblyEnabled) {
  const shipping = shippingFor(zoneId, data.zones, data.products, data.freeFrom);
  const assembly = assemblyFor(data, assemblyEnabled);
  const discount = Math.max(0, Number(data.discount) || 0);
  const total = data.products - discount + (shipping?.price ?? 0) + assembly.price;
  const saving = transferSaving(data.packLines);
  return { products: data.products, discount, shipping, assembly, total, transferTotal: saving === null ? null : total - saving };
}

const money = (value) => new Intl.NumberFormat('es-UY', { style: 'currency', currency: 'UYU', minimumFractionDigits: value % 100 ? 2 : 0, maximumFractionDigits: 2 }).format(value / 100);
const ZONE_KEY = 'cart-zone';

if (typeof customElements !== 'undefined' && !customElements.get('cart-summary')) {
  customElements.define('cart-summary', class extends HTMLElement {
    connectedCallback() {
      this.data = JSON.parse(this.querySelector('[data-summary-data]').textContent);
      this.select = this.querySelector('[data-zone]');
      this.toggle = this.querySelector('[data-assembly-toggle]');
      this.status = this.querySelector('[data-status]');
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.select.value = this.savedZone();
      this.select.addEventListener('change', () => { this.saveZone(this.select.value); this.render(); }, { signal });
      this.toggle?.addEventListener('change', () => this.onToggle(), { signal });
      this.render();
    }

    disconnectedCallback() { this.controller?.abort(); }

    savedZone() {
      try { return localStorage.getItem(ZONE_KEY) || ''; } catch { return ''; }
    }

    saveZone(value) {
      try { localStorage.setItem(ZONE_KEY, value); } catch { /* sin almacenamiento */ }
    }

    assemblyEnabled() {
      const assembly = assemblyFor(this.data, false);
      return assembly.units > 0 ? assembly.existing >= assembly.units : assembly.existing > 0;
    }

    render() {
      const enabled = this.toggle ? this.toggle.checked : this.assemblyEnabled();
      const summary = summarize(this.data, this.select.value, enabled);
      const text = this.data.text;
      const shippingLabel = summary.shipping ? (summary.shipping.free ? text.free : money(summary.shipping.price)) : text.choose_short;
      this.querySelector('[data-shipping-price]').textContent = shippingLabel;
      this.querySelector('[data-products]').textContent = money(summary.products);
      this.querySelector('[data-shipping]').textContent = summary.shipping ? (summary.shipping.free ? text.free : money(summary.shipping.price)) : text.pending;
      this.querySelector('[data-assembly]').textContent = summary.assembly.price > 0 ? money(summary.assembly.price) : text.none;
      this.querySelector('[data-total]').textContent = money(summary.total);
      // T-045: «Pagar por transferencia (WhatsApp)» del pie del carrito, con el envío y el armado elegidos.
      const wa = this.closest('[data-cart-content]')?.querySelector('[data-transfer-wa]');
      if (wa) {
        const message = transferMessage(this.data, summary);
        wa.hidden = !message;
        if (message) wa.href = message.url;
        const badge = wa.querySelector('[data-transfer-pct]');
        if (badge) {
          const pct = message ? transferPercentOf(this.data.packLines) : 0;
          badge.hidden = pct < 1;
          badge.textContent = pct < 1 ? '' : (this.data.text.pct_off || '').replace('[pct]', pct);
        }
      }
      const transferRow = this.querySelector('[data-transfer-row]');
      if (transferRow) { transferRow.hidden = summary.transferTotal === null; if (summary.transferTotal !== null) transferRow.querySelector('[data-transfer-total]').textContent = money(summary.transferTotal); }
      const discountRow = this.querySelector('[data-discount-row]');
      if (discountRow) { discountRow.hidden = summary.discount <= 0; this.querySelector('[data-discount]').textContent = `−${money(summary.discount)}`; }
      if (this.toggle) {
        const price = this.querySelector('[data-assembly-price]');
        const detail = this.querySelector('[data-assembly-detail]');
        const Packs = globalThis.Packs;
        const unit = Packs ? Packs.roundMoney(summary.assembly.unit) : summary.assembly.unit;
        const full = summary.assembly.units * unit;
        price.textContent = full > 0 ? `+ ${money(full)}` : '';
        detail.textContent = (summary.assembly.units === 1 ? text.detail_one : text.detail_other).replace('[count]', summary.assembly.units).replace('[tarifa]', money(unit));
        const regular = this.querySelector('[data-assembly-regular]');
        if (regular) regular.hidden = !summary.assembly.discounted;
        this.toggle.disabled = this.busy || (!this.toggle.checked && !summary.assembly.canToggle);
      }
    }

    async onToggle() {
      if (this.busy) return;
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.busy) { this.toggle.checked = !this.toggle.checked; return; }
      const enable = this.toggle.checked;
      const assembly = assemblyFor(this.data, enable);
      this.busy = true;
      this.toggle.disabled = true;
      this.status.textContent = this.data.text.loading;
      try {
        const root = window.Shopify?.routes?.root ?? '/';
        let response;
        if (enable) {
          if (!assembly.missing) return;
          response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: this.data.variant, quantity: assembly.missing }] }) });
        } else {
          response = await fetch(`${root}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates: { [this.data.variant]: 0 } }) });
        }
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.description || this.data.text.error);
        this.status.textContent = enable ? this.data.text.added : this.data.text.removed;
        document.dispatchEvent(new CustomEvent(enable ? 'cart:items-added' : 'cart:updated', { detail: { source: 'cart-summary' } }));
        await drawer?.open(true);
      } catch (error) {
        this.toggle.checked = !enable;
        this.status.textContent = error.message || this.data.text.error;
      } finally {
        this.busy = false;
        if (this.isConnected) this.render();
      }
    }
  });
}
